<template>
  <nav
    :class="{ 'mainnav--sticky': sticky }"
    class="mainnav"
  >
    <div class="mainnav__wrapper">
      <div class="mainnav__logo-wrapper">
        <slot name="logo">
          <nuxt-link
            :to="{ name: 'index' }"
            class="mainnav__logo"
            data-tealium-rel="navi-top.image.logo"
            title="Zur Startseite"
          >
            <SvgIcon
              :image="logo"
              no-icon-class
              aria-label="Telekom Logo"
              :aria-hidden="false"
            />
          </nuxt-link>
        </slot>
      </div>

      <div
        v-if="!hideNavi"
        class="mainnav__desktop-menus"
      >
        <slot name="menu">
          <CustomerNavigation ref="customerNavigation" />
          <div class="mainnav__inner-menus">
            <PrimaryNavigation @subnav-open="toggleSubnav" />

            <NavigationControls @toggle-mobile-nav="toggleMobileNav" />
          </div>
        </slot>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es';
import { useRoute, useRouter } from '#imports';
import focusableSelectors from 'focusable-selectors';
import PrimaryNavigation from '~/components/navigation/primary-navigation.vue';
import NavigationControls from '~/components/navigation/navigation-controls.vue';
import logo from '~/assets/images/telekom-logo-magenta-bg.svg';
import CustomerNavigation from '~/components/customer-navigation.vue';

export interface MainNavigationProps {
  sticky?: boolean;
  hideNavi?: boolean;
}
withDefaults(defineProps<MainNavigationProps>(), {});

const $route = useRoute();
const $router = useRouter();

const isMenuOpen = ref<boolean>(false);
const isSubnavOpen = ref<boolean>(false);

const toggleMobileNav = (isOpen: boolean) => {
  // on a ssr redirect, this will be called and throw an error.
  if (import.meta.server) return;
  const body = document.querySelector('body');
  if (!body) return;
  if (isOpen) {
    body.classList.add('mobile-nav-open');
    isMenuOpen.value = true;
  } else {
    body.classList.remove('mobile-nav-open');
    isMenuOpen.value = false;
  }
};

const filteredFocusableElements = computed<HTMLElement[]>(() => {
  const navigationElement = document.querySelector('.mainnav__inner-menus');
  if (!navigationElement) {
    return [];
  }

  return !isSubnavOpen.value
    ? Array.from<HTMLElement>(
        navigationElement.querySelectorAll(
          focusableSelectors
            .map(
              (sel) =>
                `${sel}:not(.main-navigation-item__subnav *):not(.main-navigation-item__icon-button--desktop *)`
            )
            .join(',')
        )
      )
    : Array.from<HTMLElement>(
        navigationElement.querySelectorAll(
          focusableSelectors
            .map(
              (sel) =>
                `${sel}:not(.main-navigation-item__icon-button):not(.main-navigation-item__icon)`
            )
            .join(',')
        )
      ).filter((el) => {
        if (el.classList.contains('main-navigation-item__link')) {
          return (
            el.closest('.main-navigation-item--expanded') !== null &&
            el.closest('.main-navigation-item__subnav') !== null
          );
        }
        if (el.classList.contains('.main-navigation-item__link--big')) {
          return el.classList.contains('.main-navigation-item__subnav-back');
        }
        return true;
      });
});

const handleKeyEvent = (event: KeyboardEvent) => {
  const firstElement = filteredFocusableElements.value[0];

  if (event.key === 'Tab') {
    const currentIndex = filteredFocusableElements.value.indexOf(document.activeElement as any);
    if (currentIndex === -1) {
      event.preventDefault();
      firstElement.focus();
      return;
    }

    if (event.shiftKey) {
      const previousIndex =
        (currentIndex - 1 + filteredFocusableElements.value.length) %
        filteredFocusableElements.value.length;
      event.preventDefault();
      filteredFocusableElements.value[previousIndex].focus();
    } else {
      const nextIndex = (currentIndex + 1) % filteredFocusableElements.value.length;
      event.preventDefault();
      filteredFocusableElements.value[nextIndex].focus();
    }
  }
  if (event.key === 'Escape') {
    const toggleButton = document.querySelector<HTMLAnchorElement>(
      '[aria-controls="primary-navigation"]'
    );
    if (toggleButton) {
      toggleButton.click();
    }
  }
};

const addEventListener = () => {
  document.addEventListener('keydown', handleKeyEvent);
};
const removeEventListener = () => {
  document.removeEventListener('keydown', handleKeyEvent);
};

const toggleSubnav = (isOpen: number) => {
  isSubnavOpen.value = isOpen > 0;
};

const navigationTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const checkNavigationStatus = () => {
  if (navigationTimeout.value) {
    clearTimeout(navigationTimeout.value);
  }
  if (window.scrollY > 70) {
    const classList = document.querySelector('body')?.classList;
    classList?.add('nav-scrolled');
    navigationTimeout.value = setTimeout(() => {
      document.querySelector('body')?.classList?.add('nav-scrolled--complete');
    }, 400);
  } else {
    const classList = document.querySelector('body')?.classList;
    if (classList) {
      classList.remove('nav-scrolled');
      classList.remove('nav-scrolled--complete');
    }
  }
};

watch(
  () => $route,
  () => {
    toggleMobileNav(false);
  }
);

watch(isMenuOpen, (val, oldValue) => {
  if (val === oldValue) return;
  if (!val) {
    removeEventListener();
  } else {
    addEventListener();
  }
});

const scroller = debounce(() => {
  checkNavigationStatus();
}, 20);

onMounted(() => {
  window.addEventListener('scroll', scroller);
});

onUnmounted(() => {
  window.removeEventListener('scroll', scroller);
  if (navigationTimeout.value) {
    clearTimeout(navigationTimeout.value);
  }
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.mainnav {
  background-color: var(--color-bg);
  color: var(--color-font);
  will-change: scroll-position;
  z-index: $z-index-navbar;
  user-select: none;
  height: 60px;
  box-shadow: 0 2px 9px 0 rgb(from var(--color-inverse-bg) r g b / 0.15);
  @include mq($mq-sm) {
    height: 64px;
  }

  @include mq($mq-lg) {
    height: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &__wrapper {
    display: flex;
    align-items: center;
    height: 100%;
    padding-right: $grid-base * 0.5;
    justify-content: space-between;

    @include mq($mq-sm) {
      padding-inline: $grid-base;
    }

    @include mq($mq-lg) {
      width: 100%;
      margin: 0 auto;
      max-width: $max-navbar-width + $grid-base * 4;
      padding-inline: 2 * $grid-base;
      justify-content: flex-start;
    }
  }

  &__logo-wrapper {
    flex: 1 1 100%;
    display: flex;
    align-self: stretch;

    @include mq($mq-lg) {
      align-self: flex-end;
      flex: initial;
      min-width: 96px;
      margin-right: $grid-base * 1.5;
    }
    @include mq($mq-lg) {
      min-width: 138px;
    }
  }

  &__logo {
    display: block;
    height: 100%;
    transition: height 0.4s $nav-timing-function;

    @include mq($mq-lg) {
      height: 96px;

      body.nav-scrolled & {
        height: 70px;
      }
    }

    > svg {
      height: 100%;
      width: auto;
    }
  }

  &__desktop-menus,
  &__inner-menus {
    height: 100%;
    @include mq($mq-lg) {
      width: 100%;
    }
  }

  &__desktop-menus {
    @include mq($mq-lg) {
      max-width: calc(100% - 156px);
    }
  }

  &__inner-menus {
    height: 100%;
    @include mq($mq-lg) {
      display: flex;
      justify-content: space-between;
      grid-gap: $grid-base * 1.5;
    }
  }
}
</style>

<style lang="scss">
@use 'assets/base' as *;
body.mobile-nav-open {
  @include mq(false, $mq-lg - 1) {
    overflow: hidden;
  }
}
</style>
